import React from 'react';
import { 
  FaBookOpen, 
  FaChalkboardTeacher, 
  FaTrophy, 
  FaPalette, 
  FaLaptop, 
  FaSeedling,
  FaStar,
  FaCheckCircle,
  FaGraduationCap,
  FaUsers,
  FaChartLine,
  FaAward
} from 'react-icons/fa';

const FeaturesSection = () => {
  const features = [
    {
      icon: <FaBookOpen className="w-6 h-6" />,
      title: "Holistic Curriculum",
      desc: "CBSE affiliated curriculum focusing on all-round development from Pre to Class X",
      gradient: "bg-gradient-to-br from-blue-50 to-blue-100",
      iconBg: "bg-gradient-to-r from-blue-500 to-blue-600",
      stats: "40+ Subjects",
      points: [
        "Comprehensive syllabus",
        "Value-based education",
        "Life skills training"
      ],
      color: "blue"
    },
    {
      icon: <FaChalkboardTeacher className="w-6 h-6" />,
      title: "Expert Faculty",
      desc: "Highly qualified teachers with 15+ years average experience and continuous training",
      gradient: "bg-gradient-to-br from-purple-50 to-purple-100",
      iconBg: "bg-gradient-to-r from-purple-500 to-purple-600",
      stats: "50+ Teachers",
      points: [
        "Specialized subject experts",
        "Regular professional development",
        "Individual attention focus"
      ],
      color: "purple"
    },
    {
      icon: <FaTrophy className="w-6 h-6" />,
      title: "Sports Excellence",
      desc: "Comprehensive sports program with professional coaching and modern facilities",
      gradient: "bg-gradient-to-br from-emerald-50 to-emerald-100",
      iconBg: "bg-gradient-to-r from-emerald-500 to-emerald-600",
      stats: "15+ Sports",
      points: [
        "Annual sports meets",
        "Professional coaches",
        "District level winners"
      ],
      color: "emerald"
    },
    {
      icon: <FaPalette className="w-6 h-6" />,
      title: "Creative Arts",
      desc: "Vibrant arts program nurturing creativity through diverse artistic expressions",
      gradient: "bg-gradient-to-br from-amber-50 to-amber-100",
      iconBg: "bg-gradient-to-r from-amber-500 to-amber-600",
      stats: "20+ Arts",
      points: [
        "Art and craft studio",
        "Music and dance rooms",
        "Annual cultural fest"
      ],
      color: "amber"
    },
    {
      icon: <FaLaptop className="w-6 h-6" />,
      title: "Smart Classes",
      desc: "Technology-enabled classrooms with interactive digital learning tools and resources",
      gradient: "bg-gradient-to-br from-indigo-50 to-indigo-100",
      iconBg: "bg-gradient-to-r from-indigo-500 to-indigo-600",
      stats: "Classrooms",
      points: [
        "Digital smart boards",
        "Computer labs",
        "E-library access"
      ],
      color: "indigo"
    },
    {
      icon: <FaSeedling className="w-6 h-6" />,
      title: "Green Campus",
      desc: "Sustainable 5-acre campus promoting environmental awareness and eco-friendly practices",
      gradient: "bg-gradient-to-br from-green-50 to-green-100",
      iconBg: "bg-gradient-to-r from-green-500 to-green-600",
      stats: "Large Campus",
      points: [
        "Solar powered facilities",
        "Organic gardens",
        "Waste management system"
      ],
      color: "green"
    }
  ];

  

  return (
    <section className="py-12 bg-white">
      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="max-w-3xl mx-auto mb-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 mb-4 text-sm font-medium text-blue-600 rounded-full bg-blue-50">
            <FaStar className="w-4 h-4" />
            <span>Why Choose Gyan Rashmi</span>
          </div>
          <h2 className="mb-4 text-3xl font-bold text-gray-900">
            Excellence in <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Modern Education</span>
          </h2>
          <p className="text-gray-600">
            A comprehensive learning environment that nurtures academic excellence, creativity, and personal growth
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {features.map((feature, index) => (
            <div 
              key={index}
              className={`group relative overflow-hidden rounded-xl border border-gray-100 ${feature.gradient} transition-all duration-300 hover:shadow-xl hover:-translate-y-2`}
            >
              {/* Card Content */}
              <div className="p-6">
                {/* Icon and Stats Header */}
                <div className="flex items-start justify-between mb-5">
                  <div className={`p-3 rounded-xl text-white shadow-sm ${feature.iconBg}`}>
                    {feature.icon}
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">{feature.stats}</div>
                    <div className="text-xs text-gray-500">Available</div>
                  </div>
                </div>

                {/* Title and Description */}
                <h3 className="mb-3 text-lg font-bold text-gray-900">{feature.title}</h3>
                <p className="mb-5 text-sm leading-relaxed text-gray-600">
                  {feature.desc}
                </p>

                {/* Points List */}
                <div className="space-y-2">
                  {feature.points.map((point, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <FaCheckCircle className={`w-4 h-4 mt-0.5 ${
                        feature.color === 'blue' ? 'text-blue-500' :
                        feature.color === 'purple' ? 'text-purple-500' :
                        feature.color === 'emerald' ? 'text-emerald-500' :
                        feature.color === 'amber' ? 'text-amber-500' :
                        feature.color === 'indigo' ? 'text-indigo-500' :
                        'text-green-500'
                      }`} />
                      <span className="text-sm text-gray-700">{point}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bottom Border Effect */}
              <div className={`absolute bottom-0 left-0 right-0 h-1 ${
                feature.color === 'blue' ? 'bg-gradient-to-r from-blue-500 to-blue-600' :
                feature.color === 'purple' ? 'bg-gradient-to-r from-purple-500 to-purple-600' :
                feature.color === 'emerald' ? 'bg-gradient-to-r from-emerald-500 to-emerald-600' :
                feature.color === 'amber' ? 'bg-gradient-to-r from-amber-500 to-amber-600' :
                feature.color === 'indigo' ? 'bg-gradient-to-r from-indigo-500 to-indigo-600' :
                'bg-gradient-to-r from-green-500 to-green-600'
              } transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500`} />

              {/* Corner Accent */}
              <div className={`absolute top-0 right-0 w-16 h-16 ${
                feature.color === 'blue' ? 'bg-blue-500/5' :
                feature.color === 'purple' ? 'bg-purple-500/5' :
                feature.color === 'emerald' ? 'bg-emerald-500/5' :
                feature.color === 'amber' ? 'bg-amber-500/5' :
                feature.color === 'indigo' ? 'bg-indigo-500/5' :
                'bg-green-500/5'
              } rounded-bl-3xl`} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;